import React, { useEffect, useState, useCallback } from "react";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  ListItemText,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications/list.php?limit=20");
      setItems(res.notifications || []);
      setUnread(res.unread || 0);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark_read.php", { all: true });
      setItems((arr) => arr.map((n) => ({ ...n, is_read: 1 })));
      setUnread(0);
    } catch {}
  };

  const handleClickItem = async (n) => {
    handleClose();
    if (!n.is_read) {
      try {
        await api.post("/notifications/mark_read.php", { id: n.id });
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen}>
          <Badge badgeContent={unread} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 360, maxHeight: 480, mt: 1 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Notifications
          </Typography>
          <Button size="small" startIcon={<MarkEmailReadIcon />} onClick={markAll}>
            Mark all
          </Button>
        </Box>
        <Divider />

        {items.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {items.map((n) => (
          <MenuItem
            key={n.id}
            onClick={() => handleClickItem(n)}
            sx={{
              whiteSpace: "normal",
              alignItems: "flex-start",
              bgcolor: n.is_read ? "transparent" : "action.hover",
              py: 1.25,
            }}
          >
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: n.is_read ? 500 : 700 }}>
                  {n.title}
                </Typography>
              }
              secondary={
                <>
                  {n.body && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {n.body}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled">
                    {new Date(n.created_at).toLocaleString()}
                  </Typography>
                </>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
